/**
 * Il marchio dell'Osservatorio Italiano.
 *
 * È un lockup in due parti: il **segno** «OI» (la O come ottagono a
 * spigoli smussati con il foro centrale, e l'asta della I accostata,
 * tagliata in diagonale in alto) e la **scritta** su due righe,
 * OSSERVATORIO sopra e ITALIANO sotto, allineata a sinistra e centrata
 * in altezza sul segno.
 *
 * ## Il segno è un disegno, la scritta è testo
 *
 * Sono due cose diverse e stanno in due modi diversi, di proposito.
 *
 * Il segno è un `<path>`: non è composto di lettere, è una figura, e
 * nessun carattere lo contiene. Le misure vengono dal PNG del logo
 * (`uploads/image.png`, 1902x827) letto pixel per pixel: il `viewBox`
 * è il rettangolo del segno senza margini, in quelle stesse unità,
 * con l'origine nel pixel (160, 230) dell'immagine.
 *
 * La scritta invece è **testo vivo in JetBrains Mono ExtraBold**, cioè
 * lo stesso carattere e lo stesso peso del titolo di apertura della
 * prima pagina (misurato: `font-weight: 800` su un h2 di 38px). Il
 * marchio scritto a mano come tracciati era un grottesco squadrato che
 * nel sito non compare da nessun'altra parte: accanto al titolo della
 * notizia si leggevano come due pubblicazioni diverse.
 *
 * Non è una dipendenza da un font di sistema: JetBrains Mono la serve
 * l'applicazione, `next/font/google` in `app/layout.tsx`, che la
 * ospita in casa e le dà un ripiego con le stesse metriche. Se non
 * arriva non cambia il marchio, cambia tutto il sito, perché il
 * `body` di `app/globals.css` è già composto con lei.
 *
 * ## Fuori dal browser il testo non regge, e c'è un file per quello
 *
 * Il ragionamento di sopra vale finché c'è un'applicazione che serve il
 * carattere. In un PDF, in un allegato, su carta, in un'immagine di
 * condivisione non c'è: là il testo vivo uscirebbe nel ripiego del
 * lettore, cioè in un grottesco qualsiasi. Per quegli usi il marchio è
 * stato convertito in curve **una volta sola**, e sta in
 * `public/marchio-osservatorio-italiano.svg` (lo rigenera
 * `assets/marchio-tracciati.mjs`, con la ricetta in testa).
 *
 * Quel file è una copia irrigidita di questo componente a corpo 25: se
 * qui cambiano il disegno o le misure, va rigenerato, altrimenti le due
 * versioni del marchio divergono. Nel sito **non si usa**, e non è
 * un'alternativa più sicura: dei tracciati non seguono i titoli, e
 * seguire i titoli è il motivo per cui la scritta è testo.
 *
 * ## Il marchio dei disegni del 15/10/2026 (vale questo)
 *
 * Le pagine consegnate il 15/10 (`uploads/Pagina della aperto
 * notizia-html.zip`, `Notizia.dc.html`, testata e piede) ridisegnano il
 * lockup, e le misure delle sezioni qui sotto restano solo come storia:
 *
 * - testata: segno 44x35 e scritta a 15px, peso 600, interlinea 1,1,
 *   spaziatura 0,02em, 12 pixel fra i due; piede: segno 36x29, scritta
 *   a 13px, 10 pixel. In `em` sono segno **2,3em** e vuoto **0,8em**
 *   (a 15px 34,5 e 12, a 13px 29,9 e 10,4: le due misure del disegno);
 * - **due colori, più l'accento**: il segno nel «mare», il triangolo in
 *   alto a destra nel rame del «segnale», la scritta nell'inchiostro.
 *   Per questo il lockup prende `tonoSegno` e `accento` oltre al colore
 *   del testo: con il solo `currentColor` tutto usciva di un tono solo,
 *   ed è il motivo per cui la testata non somigliava al disegno.
 *
 * ## Una sola manopola: il corpo
 *
 * Il marchio non si misura in altezza ma in **corpo**, e tutto il
 * resto scende in `em`. È l'unico modo perché disegno e scritta
 * crescano insieme: da una classe `h-*` il CSS non sa ricavare un
 * corpo, e il segno finirebbe alto il doppio della scritta.
 *
 * Tutte le quote sono in `em`, calcolate sull'altezza delle maiuscole
 * di JetBrains Mono, che è **0,73em** (misurata in browser con
 * `measureText`, avanzamento 0,60em, identica fra peso 700 e 800):
 *
 * | grandezza | in em | a corpo 25 |
 * | --- | --- | --- |
 * | maiuscole | 0,730 | 18,3 |
 * | interlinea | 1,120 | 28 |
 * | **altezza del segno** | **1,850** | **46,3** |
 * | **inchiostro della scritta** | **1,850** | **46,3** |
 * | scatola della scritta (due righe) | 2,240 | 56 |
 * | vuoto segno-scritta | 0,365 | 9,1 |
 *
 * Quindi `text-[25px]` dà un marchio di 56 pixel di scatola (la misura
 * della barra in alto) e `text-[14px]` uno di 31 (il piede). Per una
 * scatola voluta, il corpo è `scatola / 2,24`.
 *
 * ## Perché il segno non è alto come dice il logo
 *
 * Sul PNG il segno è 355 e l'inchiostro della scritta 243, cioè il
 * segno è una volta e mezza la scritta. Riprodotto tale e quale il
 * marchio non regge, e la ragione è il carattere: il grottesco del logo
 * è largo 0,92 maiuscole per lettera, JetBrains Mono ne è largo 0,82 ed
 * è più leggero, quindi alla stessa altezza la scritta pesa meno e il
 * segno la schiaccia.
 *
 * Qui il segno è **alto esattamente quanto l'inchiostro della scritta**
 * (1,85em tutti e due), che è il conto `interlinea + maiuscola`. Ci si
 * arriva con tre aggiustamenti insieme, perché uno solo non basta:
 *
 * - **lettere più grandi**: corpo da 22,5 a 25, maiuscole da 16,4 a
 *   18,3 pixel;
 * - **più aria fra le due righe**: interlinea da 0,976 a 1,120em, cioè
 *   il vuoto fra la linea di base di OSSERVATORIO e la cima di ITALIANO
 *   passa da un terzo a mezza maiuscola;
 * - **segno più piccolo**: da 2,492 a 1,850em, da 56 a 46 pixel.
 *
 * Alzare solo la scritta non si poteva: per portare l'inchiostro a 56
 * pixel servirebbe corpo 33, e «OSSERVATORIO» è dodici lettere
 * monospazio, quindi la riga andrebbe da 189 a 250 pixel e il marchio a
 * 330, dove a 640 di finestra ne ha 260 prima dei bottoni. Allargare
 * solo l'interlinea nemmeno: servirebbero 40 pixel di passo su 16 di
 * maiuscola, e le due parole non si leggerebbero più come un marchio.
 * Così invece la scatola resta 56 (la barra non si muove) e la
 * larghezza 256, uguale a prima.
 *
 * Sull'avanzamento il logo non si segue: a monte è 96 su 104 di
 * maiuscola, cioè 0,674em, mentre JetBrains Mono è monospazio a
 * 0,60em. Portarla lì vorrebbe `tracking-[0.074em]` e trentacinque
 * pixel di riga in più nella barra. Restano `0.03em`, che sono l'aria
 * che le maiuscole vogliono (il `body` ne toglie 0,02, quindi va
 * scritto).
 *
 * ## Colore
 *
 * `currentColor` e nient'altro, sia sul disegno sia sulla scritta. Il
 * marchio deve stare sulla carta chiara, sulla carta scura e sulla
 * fascia navy dell'intestazione, che ha toni suoi (`--color-navbar*`):
 * un colore scritto qui dentro ne servirebbe uno solo dei tre. Chi lo
 * usa gli dà il colore con una classe (`text-accent`, o un
 * `text-[var(--color-navbarInk)]` sulla fascia) e il marchio segue.
 */

/** Il segno «OI»: tre tracciati, nessuna maschera.
 *
 * Misure lette sui pixel: il segno è 445x355, l'ottagono della O è
 * smussato di 61 con l'occhiello 183x209 smussato di 20, e l'anello è
 * spesso 77 di fianco e 73 sopra e sotto.
 *
 * Il foro della O è sottratto con `evenodd` dentro lo stesso tracciato
 * e non con una `<mask>`: una maschera vuole un `id`, e un `id` in un
 * componente che compare due volte nella stessa pagina (intestazione e
 * piede) si duplica.
 *
 * La I è due pezzi separati da una fessura diagonale larga 16,5 unità:
 * il triangolo in alto e l'asta col taglio obliquo. I due tagli sono
 * paralleli (pendenza 1,04 in orizzontale per uno in verticale), ed è
 * quella fessura a far leggere il segno come due lettere accostate e
 * non come un blocco solo.
 *
 * Questi tre tracciati sono copiati **identici** in tre altri posti,
 * che vanno allineati tutti se il disegno cambia:
 *
 * - `app/icon.svg`, l'icona del sito (convenzione di Next: `icon.svg`
 *   più `apple-icon.png` e `favicon.ico` accanto, nessuna voce `icons`
 *   da scrivere nei metadata). Là il segno non è riscalato, è solo
 *   centrato in un quadrato di 625 unità, e le due immagini vanno
 *   rigenerate dall'SVG: la ricetta con `sharp` è nel commento in testa
 *   a quel file;
 * - `app/og/riparo.png/route.tsx`, la lastra di condivisione;
 * - `assets/marchio-tracciati.mjs`, che genera il marchio in tracciati
 *   (basta rilanciarlo). */
function Segno({
  className,
  titolo,
  accento,
}: {
  className: string;
  titolo: boolean;
  accento?: string;
}) {
  return (
    <svg
      viewBox="0 0 445 355"
      fill="currentColor"
      className={className}
      {...(titolo ? { role: "img" as const } : { "aria-hidden": true as const })}
    >
      {titolo && <title>Osservatorio Italiano</title>}
      <path
        fillRule="evenodd"
        d="M61 0H276L337 61V294L276 355H61L0 294V61ZM77 93V262L97 282H240L260 262V93L240 73H97Z"
      />
      {/* Il triangolo in alto a destra. Di serie ha il colore del segno;
          `accento` gli dà una classe sua (la prima pagina del 15/10/2026
          lo vuole nel rame del segnale: `fill-segnale`). */}
      <path d="M303 0H445V136Z" className={accento} />
      <path d="M357 75L445 159V355H357Z" />
    </svg>
  );
}

export function LogoOI({
  variant = "lockup",
  className,
  decorative = false,
  accento,
  tonoSegno,
  misuraSegno = "h-[2.3em] w-auto",
  vuoto = "ml-[0.8em]",
  spaziatura = "tracking-[0.02em]",
}: {
  /** `lockup` è il marchio intero (segno più le due righe di scritta),
   *  `segno` è il solo «OI», per le filigrane e i posti quadrati dove
   *  la scritta sarebbe illeggibile. */
  variant?: "lockup" | "segno";
  /** Sul `lockup` va il **corpo** (`text-[22.5px]`), non l'altezza:
   *  vedi il commento in testa. Sul `segno`, che è un solo `<svg>`,
   *  vanno `h-*` e `w-auto` come su qualunque disegno. */
  className?: string;
  /** Quando accanto al marchio c'è già il nome scritto, il marchio va
   *  nascosto ai lettori di schermo: altrimenti il nome si sente due
   *  volte di fila. Sul `lockup` il nome è testo vero, quindi serve
   *  solo dove il marchio è accompagnato da un'altra scritta. */
  decorative?: boolean;
  /** Una classe per il solo triangolo del segno (`fill-segnale` sulla
   *  prima pagina). Senza, il segno è di un colore solo. */
  accento?: string;
  /** Sul `lockup`, la classe di colore del solo segno (`text-mare`),
   *  quando deve staccarsi dalla scritta. Senza, segue il testo. */
  tonoSegno?: string;
  /** Sul `lockup`, la misura del segno e il vuoto prima della scritta,
   *  quando vanno dati in pixel invece che in `em`: i disegni del
   *  15/10/2026 danno 44x35 con 12 di vuoto in testata e 36x29 con 10 nel
   *  piede, due rapporti diversi che una quota in `em` sola non prende. */
  misuraSegno?: string;
  vuoto?: string;
  /** Sul `lockup`, la spaziatura della scritta: 0,02em in testata,
   *  quella del contenitore nel piede. */
  spaziatura?: string;
}) {
  if (variant === "segno") {
    return (
      <Segno
        className={className ?? "h-full w-auto"}
        titolo={!decorative}
        accento={accento}
      />
    );
  }

  return (
    /* `inline-flex` sta qui dentro e non fra le classi del chiamante:
       un `hidden` passato da fuori litigherebbe con lui a pari
       specificità. Chi deve nasconderlo sotto una certa larghezza lo
       avvolge in uno span suo, come fa l'intestazione. */
    <span
      className={`inline-flex items-center ${className ?? ""}`}
      {...(decorative ? { "aria-hidden": true as const } : {})}
    >
      {/* Il segno non porta il titolo: qui il nome è nella scritta
          accanto, e un `<title>` lo farebbe sentire due volte. */}
      <Segno
        className={`${misuraSegno} shrink-0 ${tonoSegno ?? ""}`}
        titolo={false}
        accento={accento}
      />
      {/* `uppercase` e non il testo già in maiuscolo: così i lettori
          di schermo leggono «Osservatorio Italiano» e non una sigla da
          compitare.

          Nessun margine negativo in coda: i disegni del 15/10/2026 non
          lo hanno, e la scatola del marchio deve misurare quanto la
          loro. */}
      <span className={`${vuoto} font-mono font-semibold uppercase leading-[1.1] ${spaziatura}`}>
        <span className="block whitespace-nowrap">Osservatorio</span>
        <span className="block whitespace-nowrap">Italiano</span>
      </span>
    </span>
  );
}
